import { useEffect, useMemo, useRef, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay, startOfDay, endOfDay } from "date-fns";
import enUS from "date-fns/locale/en-US";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { supabase } from "../../supabaseClient";
import { CalendarDays, Clock, AlertCircle } from "lucide-react";

const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const formatTime = (date) => format(new Date(date), "h:mm a");

export default function SkillsTrainerDashboard() {
  const [user, setUser] = useState(null);
  const [program, setProgram] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [notes, setNotes] = useState({});
  const [swotMap, setSwotMap] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [expandedSwot, setExpandedSwot] = useState({});
  const [selectedAthleteId, setSelectedAthleteId] = useState(null);
  const [pendingModalOpen, setPendingModalOpen] = useState(false);
  const [pendingBooking, setPendingBooking] = useState(null);
  const [modalAnchor, setModalAnchor] = useState(null);
  const calendarContainerRef = useRef(null);

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }
      setUser(user);

      const { data: programData } = await supabase
        .from("programs")
        .select("id, name")
        .eq("administrator", user.id)
        .maybeSingle();

      if (!programData?.id) {
        setLoading(false);
        return;
      }

      setProgram(programData);
    };

    init();
  }, []);

  useEffect(() => {
    if (!program?.id) return;

    const loadBookings = async () => {
      setLoading(true);
      try {
        const { data: bookingRows, error: bookingError } = await supabase
          .from("trainer_bookings")
          .select("*, users(full_name)")
          .eq("program_id", program.id)
          .order("start_at", { ascending: true });

        if (bookingError) throw bookingError;

        setBookings(bookingRows || []);

        const athleteIds = Array.from(
          new Set((bookingRows || []).map((b) => b.athlete_id).filter(Boolean))
        );

        if (athleteIds.length > 0) {
          const { data: swotRows } = await supabase
            .from("athlete_swot")
            .select("athlete_id, strengths, weaknesses, opportunities, threats")
            .eq("program_id", program.id)
            .in("athlete_id", athleteIds);

          const nextSwot = {};
          (swotRows || []).forEach((row) => {
            nextSwot[row.athlete_id] = row;
          });
          setSwotMap(nextSwot);
        }

        const bookingIds = (bookingRows || []).map((b) => b.id);
        if (bookingIds.length > 0) {
          const { data: noteRows } = await supabase
            .from("trainer_session_notes")
            .select("booking_id, notes_current, notes_next")
            .in("booking_id", bookingIds);

          const nextNotes = {};
          (noteRows || []).forEach((row) => {
            nextNotes[row.booking_id] = {
              notes_current: row.notes_current || "",
              notes_next: row.notes_next || "",
            };
          });
          setNotes(nextNotes);
        }
      } catch (err) {
        console.error("Load bookings error", err);
        setError(err.message || "Unable to load bookings.");
      } finally {
        setLoading(false);
      }
    };

    loadBookings();
  }, [program]);

  useEffect(() => {
    if (!pendingModalOpen) return;
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      setModalAnchor({ top: window.innerHeight / 2, left: window.innerWidth / 2 });
      return;
    }
    const rect = calendarContainerRef.current?.getBoundingClientRect?.();
    if (!rect) return;
    setModalAnchor({
      top: rect.top + rect.height / 2,
      left: rect.left + rect.width / 2,
    });
  }, [pendingModalOpen]);

  const events = useMemo(() => {
    return bookings.map((booking) => ({
      id: booking.id,
      title: booking.users?.full_name || "Session",
      start: new Date(booking.start_at),
      end: new Date(booking.end_at),
      status: booking.status,
    }));
  }, [bookings]);

  const dailyBookings = useMemo(() => {
    const start = startOfDay(selectedDate);
    const end = endOfDay(selectedDate);
    return bookings.filter((booking) => {
      const startAt = new Date(booking.start_at);
      return startAt >= start && startAt <= end;
    });
  }, [bookings, selectedDate]);

  useEffect(() => {
    if (dailyBookings.length === 0) {
      setSelectedAthleteId(null);
      return;
    }
    if (!selectedAthleteId || !dailyBookings.some((b) => b.athlete_id === selectedAthleteId)) {
      setSelectedAthleteId(dailyBookings[0].athlete_id);
    }
  }, [dailyBookings, selectedAthleteId]);

  const parseSwotField = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    try {
      return JSON.parse(value);
    } catch (err) {
      return [];
    }
  };

  const selectedSwot = selectedAthleteId ? swotMap[selectedAthleteId] : null;

  const updateNote = (bookingId, field, value) => {
    setNotes((prev) => ({
      ...prev,
      [bookingId]: {
        ...(prev[bookingId] || { notes_current: "", notes_next: "" }),
        [field]: value,
      },
    }));
  };

  const toggleSwot = (bookingId) => {
    setExpandedSwot((prev) => ({
      ...prev,
      [bookingId]: !prev[bookingId],
    }));
  };

  const saveNotes = async (booking) => {
    if (!program?.id || !booking?.id) return;
    setSaving(true);
    setError("");

    try {
      const payload = {
        booking_id: booking.id,
        program_id: program.id,
        athlete_id: booking.athlete_id,
        notes_current: notes[booking.id]?.notes_current || "",
        notes_next: notes[booking.id]?.notes_next || "",
      };

      const { error: saveError } = await supabase
        .from("trainer_session_notes")
        .upsert(payload, { onConflict: "booking_id" });

      if (saveError) throw saveError;
    } catch (err) {
      console.error("Save notes error", err);
      setError(err.message || "Unable to save notes.");
    } finally {
      setSaving(false);
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    if (!bookingId) return;
    setSaving(true);
    setError("");
    try {
      const { error: updateError } = await supabase
        .from("trainer_bookings")
        .update({ status })
        .eq("id", bookingId);
      if (updateError) throw updateError;

      setBookings((prev) =>
        prev.map((b) => (b.id === bookingId ? { ...b, status } : b))
      );
      if (pendingBooking?.id === bookingId) {
        setPendingBooking((prev) => (prev ? { ...prev, status } : prev));
        setPendingModalOpen(false);
      }
    } catch (err) {
      console.error("Update booking error", err);
      setError(err.message || "Unable to update booking.");
    } finally {
      setSaving(false);
    }
  };

  if (!program && !loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
          <h2 className="text-xl font-bold text-gray-900">Create your trainer profile first</h2>
          <p className="text-sm text-gray-600 mt-2">Once your profile is set up, your dashboard will appear here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900">Trainer Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">Monthly calendar with your daily client list and SWOT.</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl flex items-center gap-2">
            <AlertCircle size={16} />
            <span className="text-sm font-bold">{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div
            ref={calendarContainerRef}
            className="lg:col-span-2 bg-white rounded-2xl shadow p-4 border h-[600px] md:h-[720px] relative"
          >
            <div className="flex items-center gap-2 mb-4">
              <CalendarDays className="text-blue-500" size={20} />
              <h2 className="text-lg font-bold text-gray-900">Appointments</h2>
            </div>
            {loading ? (
              <div className="text-sm text-gray-500">Loading calendar...</div>
            ) : (
              <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                views={["month", "week", "day"]}
                selectable
                onSelectSlot={(slotInfo) => setSelectedDate(slotInfo.start)}
                onSelectEvent={(event) => {
                  setSelectedDate(event.start);
                  const booking = bookings.find((b) => b.id === event.id);
                  if (booking?.status === "pending") {
                    setPendingBooking(booking);
                    setPendingModalOpen(true);
                  }
                }}
                eventPropGetter={(event) => ({
                  style: {
                    backgroundColor:
                      event.status === "approved"
                        ? "#2563eb"
                        : event.status === "pending"
                        ? "#f59e0b"
                        : "#6b7280",
                    borderRadius: "8px",
                    border: "none",
                    color: "white",
                  },
                })}
              />
            )}
          </div>

          <div className="bg-white rounded-2xl shadow p-4 border space-y-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900">{format(selectedDate, "MMMM d")}</h2>
              <p className="text-xs text-gray-500">Daily schedule and SWOT snapshots.</p>
            </div>

            {dailyBookings.length === 0 ? (
              <div className="text-sm text-gray-500">No sessions scheduled.</div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  {dailyBookings.map((booking) => (
                    <button
                      key={booking.id}
                      type="button"
                      onClick={() => setSelectedAthleteId(booking.athlete_id)}
                      className={`w-full text-left border rounded-xl px-3 py-2 transition ${
                        selectedAthleteId === booking.athlete_id
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 bg-white hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm font-bold text-gray-900">
                            {booking.users?.full_name || "Athlete"}
                          </p>
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock size={12} />
                            {formatTime(booking.start_at)} - {formatTime(booking.end_at)}
                          </p>
                        </div>
                        <span
                          className={`text-[10px] font-bold px-2 py-1 rounded-full ${
                            booking.status === "approved"
                              ? "bg-green-100 text-green-700"
                              : booking.status === "pending"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          {booking.status}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="border border-gray-200 rounded-xl p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-gray-900">SWOT Details</h3>
                    <button
                      type="button"
                      onClick={() => toggleSwot(selectedAthleteId || "none")}
                      className="text-xs font-bold text-blue-600 hover:text-blue-800"
                    >
                      {expandedSwot[selectedAthleteId || "none"]
                        ? "Hide"
                        : "Show"}
                    </button>
                  </div>
                  {expandedSwot[selectedAthleteId || "none"] && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-gray-600">
                      <div className="space-y-1">
                        <p className="font-bold text-gray-700">Strengths</p>
                        {parseSwotField(selectedSwot?.strengths).length ? (
                          <ul className="list-disc list-inside space-y-0.5">
                            {parseSwotField(selectedSwot?.strengths).map((s, idx) => (
                              <li key={`strength-${idx}`}>{s.item || "—"}</li>
                            ))}
                          </ul>
                        ) : (
                          <p>—</p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <p className="font-bold text-gray-700">Weaknesses</p>
                        {parseSwotField(selectedSwot?.weaknesses).length ? (
                          <ul className="list-disc list-inside space-y-0.5">
                            {parseSwotField(selectedSwot?.weaknesses).map((s, idx) => (
                              <li key={`weakness-${idx}`}>{s.item || "—"}</li>
                            ))}
                          </ul>
                        ) : (
                          <p>—</p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <p className="font-bold text-gray-700">Opportunities</p>
                        {parseSwotField(selectedSwot?.opportunities).length ? (
                          <ul className="list-disc list-inside space-y-0.5">
                            {parseSwotField(selectedSwot?.opportunities).map((s, idx) => (
                              <li key={`opportunity-${idx}`}>{s.item || "—"}</li>
                            ))}
                          </ul>
                        ) : (
                          <p>—</p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <p className="font-bold text-gray-700">Threats</p>
                        {parseSwotField(selectedSwot?.threats).length ? (
                          <ul className="list-disc list-inside space-y-0.5">
                            {parseSwotField(selectedSwot?.threats).map((s, idx) => (
                              <li key={`threat-${idx}`}>{s.item || "—"}</li>
                            ))}
                          </ul>
                        ) : (
                          <p>—</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {selectedAthleteId &&
                  dailyBookings
                    .filter((booking) => booking.athlete_id === selectedAthleteId)
                    .map((booking) => {
                      const note = notes[booking.id] || {
                        notes_current: "",
                        notes_next: "",
                      };
                      return (
                        <div key={`notes-${booking.id}`} className="space-y-2">
                          <div>
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">
                              What we did today
                            </label>
                            <textarea
                              rows={2}
                              value={note.notes_current}
                              onChange={(e) =>
                                updateNote(booking.id, "notes_current", e.target.value)
                              }
                              className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">
                              Next session plan
                            </label>
                            <textarea
                              rows={2}
                              value={note.notes_next}
                              onChange={(e) =>
                                updateNote(booking.id, "notes_next", e.target.value)
                              }
                              className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => saveNotes(booking)}
                            disabled={saving}
                            className="w-full bg-blue-600 text-white text-sm font-bold py-2 rounded-lg hover:bg-blue-700 disabled:opacity-70"
                          >
                            {saving ? "Saving..." : "Save Notes"}
                          </button>
                        </div>
                      );
                    })}
              </div>
            )}
          </div>
        </div>
      </div>
      {pendingModalOpen && pendingBooking && modalAnchor && (
        <div className="fixed inset-0 z-50">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => setPendingModalOpen(false)}
            aria-label="Close modal"
          />
          <div
            className="absolute w-full max-w-sm bg-white rounded-xl shadow-2xl p-5"
            style={{
              top: `${modalAnchor.top}px`,
              left: `${modalAnchor.left}px`,
              transform: "translate(-50%, -50%)",
            }}
          >
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-900">Confirm Session</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Clock size={16} />
                  <span>
                    {format(new Date(pendingBooking.start_at), "MMM d, h:mm a")} -{" "}
                    {format(new Date(pendingBooking.end_at), "h:mm a")}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900">
                    {pendingBooking.users?.full_name || "Athlete"}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => updateBookingStatus(pendingBooking.id, "approved")}
                  disabled={saving}
                  className="w-full bg-green-600 text-white text-sm font-bold py-2 rounded-lg hover:bg-green-700 disabled:opacity-70"
                >
                  Accept Slot
                </button>
                <button
                  type="button"
                  onClick={() => updateBookingStatus(pendingBooking.id, "rejected")}
                  disabled={saving}
                  className="w-full bg-red-600 text-white text-sm font-bold py-2 rounded-lg hover:bg-red-700 disabled:opacity-70"
                >
                  Reject
                </button>
              </div>
              <button
                type="button"
                onClick={() => setPendingModalOpen(false)}
                className="w-full border border-gray-300 text-gray-700 text-sm font-bold py-2 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
